import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Typography,
  DatePicker,
  Button,
  Space,
  Table,
  message,
  Row,
  Col,
  Input,
} from "antd";
import {
  SearchOutlined,
  FileExcelOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../configs";
import { getUser } from "../../utils/DataUser";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function LaporanPertanggungjawabanStok() {
  const [range, setRange] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchData = useCallback(
    async (page = 1, pageSize = 10, dateRange = [], search = "") => {
      setLoading(true);
      try {
        const user = getUser();
        const userLevel = String(user?.id_user_level || user?.id_level || "");
        const params = { page, limit: pageSize };
        if (dateRange && dateRange.length === 2) {
          params.start_date = dateRange[0].format("YYYY-MM-DD");
          params.end_date = dateRange[1].format("YYYY-MM-DD");
        }
        if (search && search.trim()) params.search = search.trim();
        if (userLevel === "7") params.id_customer = user.username;

        const res = await axiosInstance.get("/report/stok-berjalan", {
          params,
        });
        const fetchedData = res.data.data || [];
        setData(fetchedData);
        setPagination({
          current: res.data.page || page,
          pageSize: res.data.limit || pageSize,
          total: res.data.total || fetchedData.length,
        });
      } catch (e) {
        console.error(e);
        message.error("Gagal load laporan stok");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchData(1, 10, range, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () =>
    fetchData(1, pagination.pageSize, range, searchText);
  const handleReset = () => {
    setRange([]);
    setSearchText("");
    fetchData(1, 10, [], "");
  };
  const handleTableChange = (pager) =>
    fetchData(pager.current, pager.pageSize, range, searchText);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const user = getUser();
      const userLevel = String(user?.id_user_level || user?.id_level || "");
      const params = { format: "xlsx" };
      if (range && range.length === 2) {
        params.start_date = range[0].format("YYYY-MM-DD");
        params.end_date = range[1].format("YYYY-MM-DD");
      }
      if (searchText && searchText.trim()) params.search = searchText.trim();
      if (userLevel === "7") params.id_customer = user.username;

      const response = await axiosInstance.get("/report/stok-berjalan/export", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `stok_berjalan${params.start_date || "all"}_${params.end_date || "all"}.xlsx`;
      link.click();
      message.success("Export berhasil");
    } catch (err) {
      console.error(err);
      message.error("Gagal mengunduh file excel");
    } finally {
      setExportLoading(false);
    }
  };

  // Client-side grouping for display
  const groupedData = React.useMemo(() => {
    const groups = {};
    data.forEach((row) => {
      const key = row.item_code;
      if (!groups[key]) {
        groups[key] = {
          ...row,
          history: [],
          total_masuk: 0,
          total_keluar: 0,
          current_stok: 0,
          lots: new Set(),
        };
      }
      groups[key].history.push(row);
      groups[key].total_masuk += Number(row.total_kemasan_masuk || 0);
      groups[key].total_keluar += Number(row.total_kemasan_keluar || 0);
      groups[key].current_stok += Number(row.stok_saat_ini || 0);
      if (row.lokasi_lot) groups[key].lots.add(row.lokasi_lot);
    });
    return Object.values(groups).map((g) => ({
      ...g,
      lokasi_lot_display: Array.from(g.lots).join(", "),
    }));
  }, [data]);

  const columns = [
    {
      title: "Kode",
      dataIndex: "item_code",
      width: 100,
      ellipsis: true,
      fixed: "left",
    },
    {
      title: "Nama Barang",
      dataIndex: "item_name",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Importir",
      dataIndex: "nama_importir",
      width: 150,
      ellipsis: true,
      responsive: ["md"],
    },
    { title: "Masuk", dataIndex: "total_masuk", align: "right", width: 70 },
    { title: "Keluar", dataIndex: "total_keluar", align: "right", width: 70 },
    { title: "Stok", dataIndex: "current_stok", align: "right", width: 70 },
    {
      title: "Lot",
      dataIndex: "lokasi_lot_display",
      width: 120,
      ellipsis: true,
      responsive: ["lg"],
    },
  ];

  const expandedRowRender = (record) => {
    const history = record.history || [];
    const masukItems = history.filter((h) => h.dok_pabean_masuk || h.no_bpb);
    const keluarItems = history.filter(
      (h) => h.dok_pabean_keluar || h.no_surat_jalan,
    );

    // Parse lot_detail: "OG.3:100|OG.4:50" → [{lot, qty}]
    const firstRow = history[0] || {};
    const lotRows = firstRow.lot_detail
      ? firstRow.lot_detail.split("|").map((entry) => {
          const [lot, qty] = entry.split(":");
          return { lot: lot?.trim() || "-", qty: Number(qty) || 0 };
        })
      : [];

    const border = { style: "thin" };
    void border; // keep linter happy

    return (
      <div style={{ padding: "8px 12px", background: "#f9f9f9" }}>
        {/* ── Stok Per Lot ── */}
        {lotRows.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ fontSize: 12 }}>
              Stok Per Lot
            </Text>
            <Table
              dataSource={lotRows}
              pagination={false}
              size="small"
              rowKey="lot"
              style={{ marginTop: 4 }}
              columns={[
                {
                  title: "Lokasi Lot",
                  dataIndex: "lot",
                  width: 140,
                  render: (val) => (
                    <span
                      style={{
                        display: "inline-block",
                        padding: "1px 10px",
                        background: "#e6f4ff",
                        border: "1px solid #91caff",
                        borderRadius: 4,
                        fontSize: 12,
                        color: "#0958d9",
                        fontWeight: 600,
                      }}
                    >
                      {val}
                    </span>
                  ),
                },
                {
                  title: "Jumlah",
                  dataIndex: "qty",
                  align: "right",
                  width: 90,
                  render: (val) => (
                    <span
                      style={{
                        fontWeight: 700,
                        color: val > 0 ? "#389e0d" : "#cf1322",
                      }}
                    >
                      {Number(val).toLocaleString("id-ID")}
                    </span>
                  ),
                },
              ]}
            />
          </div>
        )}

        {/* ── Pemasukan ── */}
        {masukItems.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ fontSize: 12 }}>
              Pemasukan
            </Text>
            <Table
              dataSource={masukItems}
              pagination={false}
              size="small"
              rowKey={(r, i) => `masuk-${r.id || i}`}
              scroll={{ x: 400 }}
              style={{ marginTop: 4 }}
              columns={[
                {
                  title: "Dok Masuk",
                  dataIndex: "dok_pabean_masuk",
                  width: 120,
                  ellipsis: true,
                },
                {
                  title: "Tgl",
                  dataIndex: "tgl_dok_pabean_masuk",
                  width: 80,
                  render: (val) => (val ? dayjs(val).format("DD/MM/YY") : "-"),
                },
                {
                  title: "No BPB",
                  dataIndex: "no_bpb",
                  width: 160,
                  ellipsis: true,
                },
                {
                  title: "Jml",
                  dataIndex: "total_kemasan_masuk",
                  align: "right",
                  width: 60,
                },
              ]}
            />
          </div>
        )}

        {/* ── Pengeluaran ── */}
        {keluarItems.length > 0 && (
          <div>
            <Text strong style={{ fontSize: 12 }}>
              Pengeluaran
            </Text>
            <Table
              dataSource={keluarItems}
              pagination={false}
              size="small"
              rowKey={(r, i) => `keluar-${r.id || i}`}
              scroll={{ x: 400 }}
              style={{ marginTop: 4 }}
              columns={[
                {
                  title: "Dok Keluar",
                  dataIndex: "dok_pabean_keluar",
                  width: 120,
                  ellipsis: true,
                },
                {
                  title: "Tgl",
                  dataIndex: "tgl_dok_pabean_keluar",
                  width: 80,
                  render: (val) => (val ? dayjs(val).format("DD/MM/YY") : "-"),
                },
                {
                  title: "No SJ",
                  dataIndex: "no_surat_jalan",
                  width: 160,
                  ellipsis: true,
                },
                {
                  title: "Jml",
                  dataIndex: "total_kemasan_keluar",
                  align: "right",
                  width: 60,
                },
              ]}
            />
          </div>
        )}

        {lotRows.length === 0 &&
          masukItems.length === 0 &&
          keluarItems.length === 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Tidak ada detail transaksi
            </Text>
          )}
      </div>
    );
  };

  return (
    <Card bodyStyle={{ padding: "16px" }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Title level={4} style={{ margin: 0 }}>
            Laporan Stok Barang
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Pertanggungjawaban Stok
          </Text>
        </Col>
        <Col xs={24} md={16}>
          <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
            <Input
              placeholder="Cari kode/nama..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
              style={{ width: 160 }}
              prefix={<SearchOutlined />}
            />
            <RangePicker
              value={range}
              onChange={(val) => setRange(val || [])}
              format="DD-MM-YYYY"
              allowClear
              style={{ width: 220 }}
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
            >
              Cari
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExport}
              loading={exportLoading}
              disabled={exportLoading}
            >
              {exportLoading ? "Membuat file..." : "Excel"}
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        size="small"
        bordered
        loading={loading}
        columns={columns}
        dataSource={groupedData}
        rowKey="item_code"
        expandable={{ expandedRowRender, defaultExpandAllRows: false }}
        scroll={{ x: 700 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          size: "small",
          showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total}`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
}
